// Adjacent .section--gradient blocks show one radial gradient across the full run (see layout.css).
// CSS cannot know the position of a block in its run, so this script measures each run and gives each block
// the run height and its offset in the run. Without the script, the blocks show the CSS-only band.
document.addEventListener("DOMContentLoaded", function (): void {
    const blocks: HTMLElement[] = Array.from(document.querySelectorAll<HTMLElement>(".section--gradient"));
    const runs: HTMLElement[][] = findRuns(blocks);

    if (runs.length === 0) {
        return;
    }

    // The observer calls back once per frame for all changed blocks, also once after observe().
    // A new background does not change the layout, so the callback does not start a resize loop.
    const observer: ResizeObserver = new ResizeObserver(function (): void {
        updateRuns(runs);
    });

    runs.forEach(function (run: HTMLElement[]): void {
        run.forEach(function (block: HTMLElement): void {
            observer.observe(block);
        });
    });
});

interface RunMeasurement {
    run: HTMLElement[];
    top: number;
    height: number;
    blockTops: number[];
}

/**
 * Groups the blocks into runs of adjacent siblings. Returns only runs of two or more blocks,
 * because a block alone keeps its own radial gradient.
 */
function findRuns(blocks: HTMLElement[]): HTMLElement[][] {
    const runs: HTMLElement[][] = [];

    blocks.forEach(function (block: HTMLElement): void {
        const current: HTMLElement[] | undefined = runs[runs.length - 1];

        if (current !== undefined && current[current.length - 1] === block.previousElementSibling) {
            current.push(block);
            return;
        }

        runs.push([block]);
    });

    return runs.filter(function (run: HTMLElement[]): boolean {
        return run.length > 1;
    });
}

function updateRuns(runs: HTMLElement[][]): void {
    // Read all sizes before the first write, so that the browser calculates the styles only once.
    const measurements: RunMeasurement[] = runs.map(measureRun);

    measurements.forEach(function (measurement: RunMeasurement): void {
        measurement.run.forEach(function (block: HTMLElement, index: number): void {
            block.style.setProperty("--gradient-run-height", `${measurement.height}px`);
            block.style.setProperty("--gradient-run-offset", `${measurement.blockTops[index] - measurement.top}px`);
            block.dataset.gradientRun = "";
        });
    });
}

function measureRun(run: HTMLElement[]): RunMeasurement {
    const rects: DOMRect[] = run.map(function (block: HTMLElement): DOMRect {
        return block.getBoundingClientRect();
    });
    const top: number = rects[0].top;

    return {
        run: run,
        top: top,
        height: rects[rects.length - 1].bottom - top,
        blockTops: rects.map(function (rect: DOMRect): number {
            return rect.top;
        })
    };
}
